import React from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  SimpleGrid,
  Link,
  Text,
  Badge,
  useColorMode,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { brandConfig, getColor } from "../brandConfig";

interface PageLink {
  label: string;
  path: string;
}

interface PageSection {
  title: string;
  description: string;
  pages: PageLink[];
}

const SiteMap: React.FC = () => {
  const { colorMode } = useColorMode();

  const sections: PageSection[] = [
    {
      title: "Main Pages",
      description: "Core website pages and solutions",
      pages: [
        { label: "Home", path: "/" },
        { label: "About", path: "/about" },
        { label: "Solutions Overview", path: "/solutions" },
        { label: "Solar + Property Solution", path: "/solutions/solar-property" },
        { label: "Finance + Solar Solution", path: "/solutions/finance-solar" },
        { label: "Wealth Building Solution", path: "/solutions/wealth-building" },
        { label: "First Home + Solar Solution", path: "/solutions/first-home-solar" },
        { label: "Investment + Solar/Battery Solution", path: "/solutions/investment-solar-battery-upgrade" },
        { label: "Refinance + Solar/Battery Solution", path: "/solutions/refinance-solar-battery" },
        { label: "Contact", path: "/contact" },
        { label: "Success Stories", path: "/success-stories" },
      ],
    },
    {
      title: "Solar Division",
      description: "Solar energy services and resources",
      pages: [
        { label: "Solar Home", path: "/solar" },
        { label: "Solar Services Overview", path: "/solar/services" },
        { label: "Residential Solar", path: "/solar/services/residential" },
        { label: "Commercial Solar", path: "/solar/services/commercial" },
        { label: "Battery Storage", path: "/solar/services/battery" },
        { label: "Monitoring Services", path: "/solar/services/monitoring" },
        { label: "Investment Property Solar", path: "/solar/services/investment-property" },
        { label: "Solar Process", path: "/solar/process" },
        { label: "Solar Resources", path: "/solar/resources" },
        { label: "Technology Guide", path: "/solar/resources/technology" },
        { label: "Incentives & Rebates", path: "/solar/resources/incentives" },
        { label: "Maintenance Guide", path: "/solar/resources/maintenance" },
        { label: "System Comparisons", path: "/solar/resources/comparisons" },
        { label: "Battery Guide", path: "/solar/resources/battery-guide" },
        { label: "Solar FAQ", path: "/solar/faq" },
        { label: "Solar Calculator", path: "/solar/calculator" },
        { label: "Solar Quote", path: "/solar/quote" },
        { label: "Solar Contact", path: "/solar/contact" },
        { label: "Battery Rebates Landing Page", path: "/solar/battery-rebates" },
        { label: "Battery Rebates – Not Eligible (Renter)", path: "/solar/battery-rebates/no-qualify" },
        { label: "Household IQ™ Quiz", path: "/solar/household-iq" },
        { label: "Solar Savings & Battery Rebates Ending Soon", path: "/solar/solar-savings-ending-soon" },
        { label: "Corporate Social & Environmental Responsibility", path: "/solar/cser" },
      ],
    },
    {
      title: "Finance Division",
      description: "Finance and wealth management services",
      pages: [
        { label: "Finance Home", path: "/finance" },
        { label: "First Home Finance", path: "/finance/services/first-home" },
        { label: "Mortgage Services", path: "/finance/services/mortgage" },
        { label: "Investment Property Finance", path: "/finance/services/investment" },
        { label: "Refinancing Services", path: "/finance/services/refinancing" },
        { label: "Refinance Services", path: "/finance/services/refinance" },
        { label: "Asset Finance Services", path: "/finance/services/assets" },
        { label: "Personal Finance Services", path: "/finance/services/personal" },
        { label: "Construction Loans", path: "/finance/services/construction" },
        { label: "Commercial Finance", path: "/finance/services/commercial" },
        { label: "Customer Finance", path: "/finance/services/customer-finance" },
        { label: "SMSF Finance", path: "/finance/services/smsf" },
        { label: "Personal & Asset Finance", path: "/finance/services/personal-asset" },
        { label: "Personal Loans", path: "/finance/services/personal-loans" },
        { label: "Personal Loans V2", path: "/finance/services/personal-loans-v2" },
        { label: "Bad Credit Finance", path: "/finance/services/bad-credit" },
        { label: "Solar Loans", path: "/finance/services/solar-loans" },
        { label: "Property + Solar Finance", path: "/finance/services/property-solar-finance" },
        { label: "Wealth Strategy", path: "/finance/wealth/strategy" },
        { label: "SMSF Wealth", path: "/finance/wealth/smsf" },
        { label: "Financial Planning", path: "/finance/wealth/planning" },
        { label: "Personal & Asset Finance Info", path: "/finance/personal-asset" },
        { label: "Finance Resources", path: "/finance/resources" },
        { label: "Finance FAQ", path: "/finance/faq" },
        { label: "Borrowing Power Tool", path: "/finance/tools/borrowing-power" },
        { label: "Refinance Tool", path: "/finance/tools/refinance" },
        { label: "SMSF Tool", path: "/finance/tools/smsf" },
        { label: "Stamp Duty Tool", path: "/finance/tools/stamp-duty" },
        { label: "Loan Comparison Tool", path: "/finance/tools/comparison" },
        { label: "Personal & Asset Tool", path: "/finance/tools/personal-asset" },
        { label: "Finance Application", path: "/finance/apply" },
        { label: "Finance Contact", path: "/finance/contact" },
      ],
    },
    {
      title: "Property Division",
      description: "Property investment and strategy services",
      pages: [
        { label: "Property Home", path: "/property" },
        { label: "Data-Driven Strategy", path: "/property/strategy/data-driven" },
        { label: "Investment Goals", path: "/property/strategy/goals" },
        { label: "Diversification Strategy", path: "/property/strategy/diversification" },
        { label: "Solar Value-Add Strategy", path: "/property/strategy/solar-value-add" },
        { label: "Finance Optimization", path: "/property/strategy/finance-optimization" },
        { label: "Investment Strategy Service", path: "/property/services/investment-strategy" },
        { label: "OPA Plus Service", path: "/property/services/opa-plus" },
        { label: "Tax Protection Service", path: "/property/services/tax-protection" },
        { label: "Property + Solar Package", path: "/property/services/property-solar-package" },
        { label: "Investment Financing", path: "/property/services/investment-financing" },
        { label: "Property Resources", path: "/property/resources" },
        { label: "EOI Form", path: "/property/resources/eoi-form" },
        { label: "OPA Form", path: "/property/resources/opa-form" },
        { label: "Transaction Form", path: "/property/resources/transaction-form" },
        { label: "Property FAQ", path: "/property/faq" },
        { label: "Investment Calculator", path: "/property/tools/investment-calculator" },
        { label: "Rental Yield Calculator", path: "/property/tools/rental-yield" },
        { label: "Portfolio Growth Calculator", path: "/property/tools/portfolio-growth" },
        { label: "Property + Solar Value Calculator", path: "/property/tools/property-solar-value" },
        { label: "Property Consultation", path: "/property/consultation" },
        { label: "Property Contact", path: "/property/contact" },
        { label: "Case Studies", path: "/property/case-studies" },
        { label: "Property Team", path: "/property/team" },
        { label: "Property Strategists", path: "/property/strategists" },
        { label: "Build Portal – Finance Phase", path: "/property/portal/phase2" },
        { label: "Build Portal – Construction Phase", path: "/property/portal/phase3" },
        { label: "Property Management", path: "/property/management" },
        { label: "Property Management by City", path: "/property/management/:city" },
        { label: "Easy Property Investor", path: "/property/easy-investor" },
      ],
    },
    {
      title: "Water Division",
      description: "Water filtration, purification products and education",
      pages: [
        { label: "Water Home", path: "/water" },
        { label: "Water Filters Hub", path: "/water/water-filters" },
        { label: "Water Filtration Hub", path: "/water/water-filtration" },
        { label: "Water Purifiers Hub", path: "/water/water-purifiers" },
        { label: "Under-Sink Filter System", path: "/water/water-filters/under-sink-system" },
        { label: "Under-Sink Purifier System", path: "/water/water-purifiers/under-sink-system" },
        { label: "UV Filtration System", path: "/water/water-filtration/uv-system" },
        { label: "Blog: Hard Water Australia", path: "/water/blog/hard-water-australia" },
        { label: "Blog: Seqwater Annual Report 2026", path: "/water/blog/seqwater-annual-report-2026" },
        { label: "Blog: Chlorine in Tap Water", path: "/water/blog/chlorine-in-tap-water" },
        { label: "Blog: Reverse Osmosis Guide", path: "/water/blog/reverse-osmosis-guide" },
        { label: "Blog: Is Tap Water Safe?", path: "/water/blog/tap-water-safe-australia" },
        { label: "Blog: RO vs Whole Home", path: "/water/blog/reverse-osmosis-vs-whole-home" },
        { label: "Water Quality – Gold Coast", path: "/water/water-quality/gold-coast" },
      ],
    },
    {
      title: "Legal Pages",
      description: "Legal documentation and policies",
      pages: [
        { label: "Privacy Policy", path: "/privacy-policy" },
        { label: "Terms of Service", path: "/terms-of-service" },
        { label: "Financial Services Guide", path: "/financial-services-guide" },
        { label: "Disclaimer", path: "/disclaimer" },
        { label: "Complaints Procedure", path: "/complaints" },
      ],
    },
    {
      title: "Calculator Pages",
      description: "Standalone calculator tools",
      pages: [
        { label: "Solar Savings Calculator", path: "/calculators/solar-savings" },
        { label: "Solar Battery ROI Calculator", path: "/calculators/solar-battery-roi" },
        { label: "Refinance Savings Calculator", path: "/calculators/refinance-savings" },
        { label: "Borrowing Power Calculator", path: "/calculators/borrowing-power" },
        { label: "Loan Comparison Calculator", path: "/calculators/loan-comparison" },
        { label: "Personal Asset Finance Calculator", path: "/calculators/personal-asset-finance" },
        { label: "SMSF Property Calculator", path: "/calculators/smsf-property" },
        { label: "Stamp Duty Calculator", path: "/calculators/stamp-duty" },
        { label: "Property Investment ROI Calculator", path: "/calculators/property-investment-roi" },
        { label: "Rental Yield Calculator", path: "/calculators/rental-yield" },
        { label: "Portfolio Growth Calculator", path: "/calculators/portfolio-growth" },
        { label: "Property + Solar ROI Calculator", path: "/calculators/property-solar-roi" },
        { label: "Tax Optimization Calculator", path: "/calculators/tax-optimization" },
      ],
    },
  ];

  const totalPages = sections.reduce((sum, section) => sum + section.pages.length, 0);

  return (
    <Box minH="100vh" bg="white" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl">Site Map</Heading>
            <Text fontSize="lg" maxW="2xl">
              Complete overview of all {totalPages} pages on the One Group website
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={16} flex="1">
        <VStack spacing={12} align="stretch">
          {sections.map((section, idx) => (
            <Box key={idx}>
              <VStack align="start" spacing={4} mb={6}>
                <Heading size="lg" color={brandConfig.colors.primary}>
                  {section.title}
                </Heading>
                <Text color="gray.600" fontSize="md">
                  {section.description} ({section.pages.length} pages)
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {section.pages.map((page, pageIdx) => (
                  <Link
                    key={pageIdx}
                    href={page.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={getColor("border.light", colorMode)}
                    bg="white"
                    transition="all 0.2s ease"
                    _hover={{
                      bg: `${brandConfig.colors.primary}10`,
                      borderColor: brandConfig.colors.primary,
                      transform: "translateY(-2px)",
                      boxShadow: "md",
                    }}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <Text
                        color={brandConfig.colors.primary}
                        fontSize="sm"
                        fontWeight="500"
                      >
                        {page.label} →
                      </Text>
                      <Badge
                        colorScheme={page.path === "/" ? "green" : "orange"}
                        fontSize="2xs"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                      >
                        {page.path === "/" ? "done" : "Draft"}
                      </Badge>
                    </Box>
                    <Text
                      color="gray.500"
                      fontSize="xs"
                      fontFamily="monospace"
                    >
                      {page.path}
                    </Text>
                  </Link>
                ))}
              </SimpleGrid>
            </Box>
          ))}
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SiteMap;
